import { useState, useCallback, useRef } from 'preact/hooks'
import { landingPagesApi } from '@/api/landingPages'
import { useToast } from '@/hooks/useToast'
import { getDisplayErrorMessages, isValidationError } from '@/utils/errorHandling'
import type {
    LandingPageViewDto,
    LandingPageGenerateRequestDto,
    LandingPageSettingsUpdateDto,
    LandingPageContextDto,
    LandingPageContextUpdateDto,
    LandingPageDesignAnswerDto,
    LandingPageMode,
    ThankYouPageSettingsUpdateDto,
    PageMetaUpdateDto,
} from '@/types/landingPage'

interface UseLandingPageParams {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
}

export interface UseLandingPageResult {
    landingPage: LandingPageViewDto | null
    setLandingPage: (lp: LandingPageViewDto | null) => void
    html: string | null
    context: LandingPageContextDto | null
    loading: boolean
    contextLoading: boolean
    generating: boolean
    error: string | null
    load: () => Promise<LandingPageViewDto | null>
    /**
     * `load`, queued behind any saves in flight, so a slow read can't restore an ETag a save replaced.
     * A background re-read: a request that never reached the server resolves null without a toast.
     */
    loadBehindSaves: () => Promise<LandingPageViewDto | null>
    loadDraft: () => Promise<void>
    loadContext: () => Promise<void>
    /** The ETag is supplied by the hook, which tracks the newest one the server returned. */
    updateContext: (dto: Omit<LandingPageContextUpdateDto, 'ETag'>) => Promise<void>
    /** Answers Webster's outstanding design questions and restarts generation. Null when not accepted. */
    submitDesignAnswers: (
        answers: LandingPageDesignAnswerDto[],
        note: string | null,
        letWebsterDecide?: boolean,
    ) => Promise<LandingPageViewDto | null>
    createAndGenerate: (request: LandingPageGenerateRequestDto) => Promise<boolean>
    /** `useCurrentBrand` upgrades the page to the knowledge base's current brand as part of the rebuild. */
    regenerate: (prompt?: string, useCurrentBrand?: boolean) => Promise<boolean>
    updateSettings: (dto: Omit<LandingPageSettingsUpdateDto, 'ETag'>) => Promise<void>
    applyFont: (headingFamily: string | null, bodyFamily: string | null) => Promise<boolean>
    thankYouHtml: string | null
    loadThankYouDraft: () => Promise<void>
    updateThankYouSettings: (dto: Omit<ThankYouPageSettingsUpdateDto, 'ETag'>) => Promise<void>
    updatePageMeta: (dto: Omit<PageMetaUpdateDto, 'ETag'>) => Promise<void>
    regenerateThankYou: (prompt?: string) => Promise<void>
    deleteLandingPage: () => Promise<void>
    setMode: (mode: LandingPageMode, externalUrl?: string) => Promise<LandingPageViewDto | null>
}

export function useLandingPage({ clientCompanyId, workspaceId, campaignId, webId }: UseLandingPageParams): UseLandingPageResult {
    const { showError, showSuccess } = useToast()

    const [landingPage, setLandingPageState] = useState<LandingPageViewDto | null>(null)
    const [html, setHtml] = useState<string | null>(null)
    const [thankYouHtml, setThankYouHtml] = useState<string | null>(null)
    const [context, setContext] = useState<LandingPageContextDto | null>(null)
    const [loading, setLoading] = useState(false)
    const [contextLoading, setContextLoading] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const hasLoadedOnce = useRef(false)

    // ── Optimistic concurrency, owned here rather than by the callers ──
    //
    // Every write sends the ETag it believes the stored page has. A caller reading it off
    // `landingPage` sends whatever its last render saw, which is already stale by the time a
    // second save goes out — the first save has moved the stored ETag on. The builder's panels
    // save as you type, so that was every keystroke after the first (ORB-760).
    //
    // The ref holds the newest ETag the server has handed back, and each save reads it at the
    // moment it is sent. Callers pass the fields they are changing and nothing else.
    const etagRef = useRef<string | undefined>(undefined)

    const setLandingPage = useCallback((lp: LandingPageViewDto | null) => {
        etagRef.current = lp?.ETag
        setLandingPageState(lp)
    }, [])

    /**
     * Reports a failed request — unless the failure is a validation error, which is already on
     * screen.
     *
     * apiClient hands a 422 to the global validation handler, which renders the server's own
     * wording once per field. Toasting here as well showed the user the same failure twice, and the
     * second copy was the raw array `stringifyApiError` had flattened it into:
     * `Update failed [{"Title":…,"Message":…,"FieldName":…}]` (ORB-760).
     *
     * The remaining business branches (402, 403, 409, 410, 412) serialize an OBJECT into the
     * envelope's `error`, so the raw string reads `{"error":"ConcurrencyConflict","message":…}`.
     * getDisplayErrorMessages unwraps both shapes to the sentence written for the reader.
     *
     * EVERY response error this hook surfaces goes through here. Routing only the obvious writes
     * left the create path toasting a raw array of its own, because CreateAndGenerateAsync answers
     * 422 by design for a webId that names no Web (ORB-506) — so the rule is the whole surface, not
     * a list of paths someone has to remember to extend.
     */
    const showRequestError = useCallback((
        res: { error?: string; status?: number },
        title: string,
        fallback: string,
    ) => {
        if (isValidationError(res.status)) return
        showError(title, getDisplayErrorMessages(res.error, fallback).join(' '))
    }, [])

    // Writes all target the one landing-page document. Two in flight at once means the second
    // was addressed to an ETag the first is about to replace, so they are queued: each save
    // waits for its predecessor to land, and therefore for the ETag it returns.
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve())
    const enqueueSave = useCallback(<T,>(run: () => Promise<T>): Promise<T> => {
        const next = saveQueueRef.current.then(run, run)
        saveQueueRef.current = next.catch(() => undefined)
        return next
    }, [])

    const read = useCallback(async (background: boolean): Promise<LandingPageViewDto | null> => {
        // Only show loading spinner on initial load, not on reloads after generation
        if (!hasLoadedOnce.current) setLoading(true)
        setError(null)
        const res = await landingPagesApi.getLandingPage(workspaceId, webId)
        if (res.data) {
            setLandingPage(res.data)
        } else if (background && res.status === 0) {
            // The builder re-reads every few seconds while Webster works (ORB-958). Offline, each
            // read used to toast, so a short drop stacked them up (ORB-1000); the next read retries.
            console.warn('Landing page re-read skipped, no connection:', res.error)
        } else if (res.status !== 404) {
            const message = getDisplayErrorMessages(res.error, 'Failed to load landing page').join(' ')
            setError(message)
            showRequestError(res, 'Load failed', 'Failed to load landing page')
        }
        if (!hasLoadedOnce.current) {
            hasLoadedOnce.current = true
            setLoading(false)
        }
        return res.data ?? null
    }, [workspaceId, webId])

    const load = useCallback(() => read(false), [read])
    const loadBehindSaves = useCallback(() => enqueueSave(() => read(true)), [enqueueSave, read])

    const loadDraft = useCallback(async () => {
        const res = await landingPagesApi.getLandingPageDraft(workspaceId, webId)
        if (res.data) {
            setHtml(res.data as string)
        } else if (res.status !== 404 && res.error) {
            showRequestError(res, 'Failed to load preview', 'Unable to load the preview')
        }
    }, [workspaceId, webId])

    const loadContext = useCallback(async () => {
        setContextLoading(true)
        const res = await landingPagesApi.getContext(clientCompanyId, workspaceId, campaignId, webId)
        if (res.data) {
            setContext(res.data)
        } else if (res.error) {
            console.warn('Failed to load context:', res.error)
        }
        setContextLoading(false)
    }, [clientCompanyId, workspaceId, campaignId, webId])

    const updateContext = useCallback(async (dto: Omit<LandingPageContextUpdateDto, 'ETag'>) => {
        await enqueueSave(async () => {
            const etag = etagRef.current
            if (!etag) {
                showError('Update failed', 'Landing page not loaded')
                return
            }
            const res = await landingPagesApi.updateContext(workspaceId, webId, { ...dto, ETag: etag })
            if (res.data) {
                // Deliberately does NOT adopt res.data.Context. The response is this request's own
                // payload echoed back, so it carries nothing the caller does not already hold — but
                // it is a snapshot from when the request left, missing anything typed since. Feeding
                // it back reverted those edits (ORB-760). The one thing worth having, the new ETag,
                // arrives through setLandingPage.
                setLandingPage(res.data)
            } else {
                showRequestError(res, 'Update failed', 'Unable to update context')
            }
        })
    }, [workspaceId, webId, enqueueSave, setLandingPage])

    /**
     * Answers Webster's outstanding design questions and restarts generation. The server
     * requeues the build, so the LP comes back on Generating rather than finished. Resolves
     * to the updated LP, or null when the answers were not accepted.
     */
    const submitDesignAnswers = useCallback(async (
        answers: LandingPageDesignAnswerDto[],
        note: string | null,
        letWebsterDecide = false,
    ): Promise<LandingPageViewDto | null> => {
        // Queued for the same reason as the panel saves: it carries an ETag and replaces the
        // same document, so it has to take its turn rather than race a debounced edit.
        return enqueueSave(async () => {
            const etag = etagRef.current
            if (!etag) {
                showError('Update failed', 'Landing page not loaded')
                return null
            }
            const res = await landingPagesApi.submitDesignAnswers(workspaceId, webId, {
                ETag: etag,
                Answers: answers,
                Note: note,
                LetWebsterDecide: letWebsterDecide,
            })
            if (res.data) {
                setLandingPage(res.data)
                if (res.data.Context) setContext(res.data.Context)
                return res.data
            }
            showRequestError(res, 'Could not send your answers', 'Unable to submit design answers')
            return null
        })
    }, [workspaceId, webId, enqueueSave, setLandingPage])

    const createAndGenerate = useCallback(async (request: LandingPageGenerateRequestDto) => {
        setGenerating(true)
        setError(null)
        const createRes = await landingPagesApi.createLandingPage(clientCompanyId, workspaceId, campaignId, webId, request)
        if (createRes.error) {
            setGenerating(false)
            showRequestError(createRes, 'Create failed', 'Unable to create landing page')
            return false
        }
        if (createRes.data) {
            setLandingPage(createRes.data)
            if (createRes.data.Context) {
                setContext(createRes.data.Context)
            }
            // Don't load HTML here — generation is async (~2 min).
            // SignalR onStreamResult will load HTML when the AI finishes.
        }
        setGenerating(false)
        return !!createRes.data
    }, [clientCompanyId, workspaceId, campaignId, webId])

    /** Fire-and-forget for 202-Accepted endpoints where SignalR drives state updates. */
    const check202 = useCallback((res: { error?: string; status?: number }, errorTitle: string) => {
        if (res.error && res.status !== 202) {
            showRequestError(res, errorTitle, 'Request failed')
        }
    }, [])

    const regenerate = useCallback(async (prompt?: string, useCurrentBrand?: boolean) => {
        setGenerating(true)
        try {
            const res = await landingPagesApi.generateLandingPage(workspaceId, webId, prompt, useCurrentBrand)
            if (res.status === 202) return true
            // A duplicate from another tab joins the existing generation. Other conflicts
            // still fail normally — their state does not establish that Webster is working.
            if (res.status === 409 && res.error === 'GenerationAlreadyRunning') return true
            const fallback = 'Please retry. If this keeps happening, contact support.'
            if (!res.status || res.status >= 500) showError('Generation could not start', fallback)
            else showRequestError(res, 'Generation could not start', fallback)
            return false
        } finally {
            setGenerating(false)
        }
    }, [workspaceId, webId, showRequestError, showError])

    const updateSettings = useCallback(async (dto: Omit<LandingPageSettingsUpdateDto, 'ETag'>) => {
        await enqueueSave(async () => {
            const etag = etagRef.current
            if (!etag) {
                showError('Update failed', 'Landing page not loaded')
                return
            }
            const res = await landingPagesApi.updateSettings(workspaceId, webId, { ...dto, ETag: etag })
            if (res.data) {
                setLandingPage(res.data)
                showSuccess('Settings updated', 'Landing page settings have been saved.')
            } else {
                showRequestError(res, 'Update failed', 'Unable to update settings')
            }
        })
    }, [workspaceId, webId, enqueueSave, setLandingPage])

    // Applies a font choice to an existing page. The server re-bakes the draft, so we
    // reload it to update the preview iframe instantly (no regeneration). Post-generation
    // only — pre-generation the choice rides into the Generate request body instead.
    const applyFont = useCallback(async (headingFamily: string | null, bodyFamily: string | null) => {
        // Queued with the rest: this replaces the same document and returns a new ETag, so a
        // debounced panel edit landing beside it would otherwise be judged against the ETag this
        // write had already moved on. The draft reload stays outside the queue — it is a blob
        // fetch, and holding the queue open on it would stall the next save for no benefit.
        const applied = await enqueueSave(async () => {
            const res = await landingPagesApi.applyFont(workspaceId, webId, {
                HeadingFamily: headingFamily,
                BodyFamily: bodyFamily,
            })
            if (res.data) {
                setLandingPage(res.data)
                return true
            }
            showRequestError(res, 'Font change failed', 'Unable to apply the selected font')
            return false
        })
        if (applied) await loadDraft()
        return applied
    }, [workspaceId, webId, loadDraft, enqueueSave, setLandingPage])

    const loadThankYouDraft = useCallback(async () => {
        const res = await landingPagesApi.getThankYouPageDraft(workspaceId, webId)
        if (res.data) {
            setThankYouHtml(res.data as string)
        } else if (res.status !== 404 && res.error) {
            showRequestError(res, 'Failed to load thank you page preview', 'Unable to load the preview')
        }
    }, [workspaceId, webId])

    const updateThankYouSettings = useCallback(async (dto: Omit<ThankYouPageSettingsUpdateDto, 'ETag'>) => {
        await enqueueSave(async () => {
            const etag = etagRef.current
            if (!etag) {
                showError('Update failed', 'Landing page not loaded')
                return
            }
            const res = await landingPagesApi.updateThankYouSettings(workspaceId, webId, { ...dto, ETag: etag })
            if (res.data) {
                // Deliberately silent, as the Context and Page Meta saves beside it always were.
                // This panel saves on a debounce as the user types, so a success toast fired once
                // per pause in their typing — a stack of them for one sentence, announcing
                // something the user did not ask for and cannot act on (ORB-760). A failure still
                // speaks; a save doing its job does not.
                setLandingPage(res.data)
            } else {
                showRequestError(res, 'Update failed', 'Unable to update thank you settings')
            }
        })
    }, [workspaceId, webId, enqueueSave, setLandingPage])

    const updatePageMeta = useCallback(async (dto: Omit<PageMetaUpdateDto, 'ETag'>) => {
        await enqueueSave(async () => {
            const etag = etagRef.current
            if (!etag) {
                showError('Update failed', 'Landing page not loaded')
                return
            }
            const res = await landingPagesApi.updatePageMeta(workspaceId, webId, { ...dto, ETag: etag })
            if (res.data) {
                setLandingPage(res.data)
            } else {
                showRequestError(res, 'Update failed', 'Unable to update page meta')
            }
        })
    }, [workspaceId, webId, enqueueSave, setLandingPage])

    const regenerateThankYou = useCallback(async (prompt?: string) => {
        const res = await landingPagesApi.regenerateThankYou(workspaceId, webId, prompt)
        check202(res, 'Generate failed')
    }, [workspaceId, webId, check202])

    const setMode = useCallback(async (mode: LandingPageMode, externalUrl?: string): Promise<LandingPageViewDto | null> => {
        const res = await landingPagesApi.setMode(workspaceId, campaignId, webId, {
            ETag: etagRef.current,
            Mode: mode,
            ExternalUrl: externalUrl,
        })
        if (res.data) {
            setLandingPage(res.data)
            showSuccess(
                mode === 'External' ? 'External URL set' : 'Switched to Builder',
                mode === 'External'
                    ? 'Landing page now uses your custom URL.'
                    : 'Landing page switched back to the AI builder.',
            )
            return res.data
        }
        showRequestError(res, 'Update failed', 'Unable to update landing page mode')
        return null
    }, [workspaceId, campaignId, webId, setLandingPage])

    const deleteLandingPage = useCallback(async () => {
        const res = await landingPagesApi.deleteLandingPage(workspaceId, webId)
        if (res.status === 204 || res.status === 200) {
            setLandingPage(null)
            setHtml(null)
            setThankYouHtml(null)
            setContext(null)
            showSuccess('Deleted', 'Landing page and all versions have been removed.')
        } else {
            showRequestError(res, 'Delete failed', 'Unable to delete landing page')
        }
    }, [workspaceId, webId])

    return {
        landingPage,
        setLandingPage,
        html,
        context,
        loading,
        contextLoading,
        generating,
        error,
        load,
        loadBehindSaves,
        loadDraft,
        loadContext,
        updateContext,
        submitDesignAnswers,
        createAndGenerate,
        regenerate,
        thankYouHtml,
        loadThankYouDraft,
        updateThankYouSettings,
        updatePageMeta,
        regenerateThankYou,
        updateSettings,
        applyFont,
        deleteLandingPage,
        setMode,
    }
}
